/**
 * Keep the theme cart UI in sync with cart mutations made by third-party apps.
 *
 * Some apps add a paid item and a gift in separate asynchronous requests. The
 * theme can refresh after the first request and render an intermediate cart.
 * This bridge waits until all observed cart mutations have settled, then asks
 * the theme to rebuild the cart drawer from Shopify's latest cart state.
 */
(function externalCartSync() {
  'use strict';

  window.theme = window.theme || {};

  const cartOpenGestureWindow = 5000;
  let lastUserInteractionAt = 0;

  function rememberUserInteraction(event) {
    if (event.isTrusted) lastUserInteractionAt = Date.now();
  }

  document.addEventListener('pointerdown', rememberUserInteraction, true);
  document.addEventListener('keydown', rememberUserInteraction, true);

  // Compatibility with the callback currently configured in AIOD.
  window.cart = window.cart && typeof window.cart === 'object' ? window.cart : {};
  if (typeof window.cart.open !== 'function') {
    window.cart.open = function openThemeCartDrawer() {
      // AIOD can invoke this callback while it initializes on page load.
      // Only treat it as an intentional open when the visitor just interacted.
      if (Date.now() - lastUserInteractionAt > cartOpenGestureWindow) return;

      const cartDrawer = document.querySelector('cart-drawer');
      cartDrawer?.dispatchEvent(new CustomEvent('theme:cart-drawer:show', {bubbles: true}));
    };
  }

  if (window.theme.externalCartSyncInstalled) return;
  window.theme.externalCartSyncInstalled = true;

  const cartMutationPath = /\/cart\/(add|change|update|clear)(\.js)?$/i;
  const settleDelay = 800;
  const refreshConfirmationTimeout = 2500;
  const maxRefreshAttempts = 3;

  let pendingMutations = 0;
  let mutationVersion = 0;
  let successfulMutationObserved = false;
  let settleTimer = null;
  let confirmationTimer = null;
  let retryTimer = null;
  let cartChangeHandler = null;

  function isCartMutationRequest(input, options) {
    try {
      const requestUrl = typeof input === 'string' ? input : input?.url;
      const requestMethod = options?.method || input?.method || 'GET';
      const pathname = new URL(requestUrl, window.location.origin).pathname.replace(/\/$/, '');

      return requestMethod.toUpperCase() !== 'GET' && cartMutationPath.test(pathname);
    } catch (error) {
      return false;
    }
  }

  function clearRefreshConfirmation() {
    if (confirmationTimer) {
      window.clearTimeout(confirmationTimer);
      confirmationTimer = null;
    }

    if (retryTimer) {
      window.clearTimeout(retryTimer);
      retryTimer = null;
    }

    if (cartChangeHandler) {
      document.removeEventListener('theme:cart:change', cartChangeHandler);
      cartChangeHandler = null;
    }
  }

  function mutationStarted() {
    pendingMutations += 1;
    mutationVersion += 1;

    if (settleTimer) {
      window.clearTimeout(settleTimer);
      settleTimer = null;
    }

    clearRefreshConfirmation();
  }

  function requestThemeCartRefresh(version, attempt) {
    if (version !== mutationVersion || pendingMutations > 0) return;

    const cartItems = document.querySelector('cart-items');
    if (!cartItems) {
      if (attempt < maxRefreshAttempts) {
        retryTimer = window.setTimeout(
          () => requestThemeCartRefresh(version, attempt + 1),
          attempt * 800
        );
      }
      return;
    }

    clearRefreshConfirmation();

    cartChangeHandler = function onThemeCartChange() {
      if (version !== mutationVersion) return;
      clearRefreshConfirmation();
    };

    document.addEventListener('theme:cart:change', cartChangeHandler);
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));

    // The theme emits theme:cart:change after its section request succeeds.
    // Retry with a small backoff if that request was rate-limited or failed.
    confirmationTimer = window.setTimeout(() => {
      if (cartChangeHandler) {
        document.removeEventListener('theme:cart:change', cartChangeHandler);
        cartChangeHandler = null;
      }

      confirmationTimer = null;

      if (version === mutationVersion && pendingMutations === 0 && attempt < maxRefreshAttempts) {
        retryTimer = window.setTimeout(
          () => requestThemeCartRefresh(version, attempt + 1),
          attempt * 1000
        );
      }
    }, refreshConfirmationTimeout);
  }

  function mutationFinished(successful) {
    pendingMutations = Math.max(0, pendingMutations - 1);
    successfulMutationObserved = successfulMutationObserved || successful;

    if (pendingMutations > 0 || !successfulMutationObserved) return;

    const version = mutationVersion;
    successfulMutationObserved = false;

    settleTimer = window.setTimeout(() => {
      settleTimer = null;
      requestThemeCartRefresh(version, 1);
    }, settleDelay);
  }

  const originalFetch = window.fetch;
  if (typeof originalFetch === 'function') {
    window.fetch = function cartAwareFetch(input, options) {
      const observesCartMutation = isCartMutationRequest(input, options);

      if (!observesCartMutation) {
        return originalFetch.apply(this, arguments);
      }

      mutationStarted();

      return originalFetch.apply(this, arguments).then(
        (response) => {
          mutationFinished(response.ok);
          return response;
        },
        (error) => {
          mutationFinished(false);
          throw error;
        }
      );
    };
  }

  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function cartAwareXhrOpen(method, url) {
    this.__themeObservesCartMutation = isCartMutationRequest(url, {method: method});
    return originalXhrOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function cartAwareXhrSend() {
    if (!this.__themeObservesCartMutation) {
      return originalXhrSend.apply(this, arguments);
    }

    mutationStarted();

    this.addEventListener(
      'loadend',
      () => mutationFinished(this.status >= 200 && this.status < 300),
      {once: true}
    );

    try {
      return originalXhrSend.apply(this, arguments);
    } catch (error) {
      mutationFinished(false);
      throw error;
    }
  };
})();
